import {
  DataSyncViewModel,
  FieldMappingEntry,
} from '../types/DataSyncViewModel';
import { DataSyncItem, OptimizelyProduct } from '../types/DataSyncItem';
import { FieldMappingConverter } from './fieldMappingConverter';
import { FieldDefinition } from '../types/FieldDefinition';
import { filterOrphanedMappings } from '../lib/dataSync';

export class DataSyncViewModelConverter {
  /**
   * Converts a backend DataSyncItem to a ViewModel
   */
  public static fromBackend(backendModel: DataSyncItem): DataSyncViewModel {
    // Convert field mappings from backend nested format to ViewModel array format
    const fieldMappings: FieldMappingEntry[] =
      FieldMappingConverter.getFieldMappingsForUI(backendModel.field_mappings);

    return {
      id: backendModel.id,
      isNew: !backendModel.id,
      name: backendModel.name,
      description: undefined, // Not present in current backend model
      source: backendModel.source,
      destination: backendModel.destination,
      fieldMappings,
      enabled: backendModel.enabled,
      interval: backendModel.interval,
      filterConfig: backendModel.filter_config,
    };
  }

  /**
   * Converts a ViewModel back to backend DataSyncItem format
   */
  public static toBackend(
    viewModel: DataSyncViewModel,
    originalBackendModel?: DataSyncItem,
    sourceFields?: FieldDefinition[],
    destinationFields?: FieldDefinition[],
  ): DataSyncItem {
    // Filter out orphaned mappings whose destination fields no longer exist in schema,
    // then drop disabled mappings. `disabled` has no representation in the backend
    // nested_mappings shape, so a disabled mapping that reaches the API is persisted as an
    // ordinary one and comes back enabled on the next load (OCP-2313). Filtering here rather
    // than in a destination-specific save branch covers every save path.
    const activeMappings = (destinationFields
      ? filterOrphanedMappings(viewModel.fieldMappings, destinationFields)
      : viewModel.fieldMappings
    ).filter((mapping) => !mapping.disabled);

    // Convert field mappings from UI array format to backend nested format
    const uiMappingsForConverter = activeMappings.map((mapping) => ({
      destinationField: mapping.destinationField,
      sourceField: mapping.sourceField,
      disabled: mapping.disabled,
    }));

    const backendFieldMappings = FieldMappingConverter.convertFromUIFormat(
      uiMappingsForConverter,
      sourceFields,
      destinationFields
    );

    // If we have an original backend model, preserve its structure and update only what changed
    if (originalBackendModel) {
      return new DataSyncItem({
        ...originalBackendModel,
        id: viewModel.id,
        name: viewModel.name,
        enabled: viewModel.enabled,
        source: viewModel.source,
        destination: viewModel.destination,
        field_mappings: backendFieldMappings,
        interval: viewModel.interval,
        filter_config: viewModel.filterConfig,
      });
    }

    // Create a minimal backend model structure (this would need more fields in practice)
    return new DataSyncItem({
      id: viewModel.id,
      name: viewModel.name,
      enabled: viewModel.enabled,
      tracker_id: '',
      source: viewModel.source,
      destination: viewModel.destination,
      field_mappings: backendFieldMappings,
      interval: viewModel.interval,
      filter_config: viewModel.filterConfig,
    });
  }

  /**
   * Creates an empty ViewModel with default values
   */
  public static createEmpty(): DataSyncViewModel {
    return {
      name: '',
      isNew: true,
      source: {
        product: OptimizelyProduct.HUB,
        instance: { id: '', name: '' },
        config: {},
        object: {
          object_name: '',
          object_display_name: '',
          object_icon_url: '',
          app_id: '',
        },
      },
      destination: {
        product: OptimizelyProduct.HUB,
        instance: { id: '', name: '' },
        config: {},
        object: { object_name: '', object_icon_url: '' },
      },
      fieldMappings: [],
      enabled: true,
    };
  }

  /**
   * Clones a ViewModel to avoid mutation
   */
  public static clone(viewModel: DataSyncViewModel): DataSyncViewModel {
    return {
      ...viewModel,
      source: {
        ...viewModel.source,
        instance: { ...viewModel.source.instance },
        config: { ...viewModel.source.config },
        object: { ...viewModel.source.object },
      },
      destination: {
        ...viewModel.destination,
        instance: { ...viewModel.destination.instance },
        config: { ...viewModel.destination.config },
        object: { ...viewModel.destination.object },
      },
      fieldMappings: viewModel.fieldMappings.map((mapping) => ({ ...mapping })),
      filterConfig: viewModel.filterConfig ? { ...viewModel.filterConfig } : undefined,
    };
  }
}
